import {
  Alert,
  Box,
  Chip,
  Collapse,
  Divider,
  Link,
  Switch,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AutoAwesomeOutlined,
  EditNoteOutlined,
  MailOutline,
  RadioButtonChecked,
  RadioButtonUnchecked,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { Dispatch, SetStateAction } from "react";
import RecipientsInput from "./recipientsInput";
import TemplateEditor from "./templateEditor";
import { useEmailHealth } from "./hooks";
import { byteSize, isRecipientInvalid } from "./template";
import {
  EmailFormData,
  EmailMode,
  EmailPreviewResponse,
  EmailVariablesCatalog,
  MAX_BODY_BYTES,
  MAX_SUBJECT,
} from "./types";

export const isEmailStepInvalid = (form: EmailFormData) => {
  if (!form.enabled) return false;
  if (!form.recipients.some((r) => r.ativo)) return true;
  if (form.recipients.some(isRecipientInvalid)) return true;
  if (form.mode === "custom") {
    if (form.assunto.length > MAX_SUBJECT) return true;
    if (byteSize(form.corpo) > MAX_BODY_BYTES) return true;
  }
  return false;
};

interface EmailStepProps {
  value: EmailFormData;
  onChange: Dispatch<SetStateAction<EmailFormData>>;
  catalog: EmailVariablesCatalog | null;
  preview: EmailPreviewResponse | null;
  isEditing?: boolean;
}

export default function EmailStep({ value, onChange, catalog, preview, isEditing }: EmailStepProps) {
  const theme = useTheme();
  const healthy = useEmailHealth();
  const variables = catalog?.variaveis ?? [];
  const custom = value.mode === "custom";
  const subjectTooLong = value.assunto.length > MAX_SUBJECT;
  const bodyTooBig = byteSize(value.corpo) > MAX_BODY_BYTES;
  const noActive = value.recipients.length > 0 && !value.recipients.some((r) => r.ativo);

  const patch = (p: Partial<EmailFormData>) => onChange((prev) => ({ ...prev, ...p }));

  const restoreDefault = () =>
    catalog &&
    patch({ assunto: catalog.templatePadrao.assunto, corpo: catalog.templatePadrao.corpo });

  const selectMode = (mode: EmailMode) => {
    if (mode === "custom" && catalog && !value.assunto.trim() && !value.corpo.trim()) {
      patch({ mode, assunto: catalog.templatePadrao.assunto, corpo: catalog.templatePadrao.corpo });
      return;
    }
    patch({ mode });
  };

  const modeCard = (
    mode: EmailMode,
    icon: React.ReactNode,
    title: string,
    description: string,
    badge?: string,
  ) => {
    const selected = value.mode === mode;
    return (
      <Box
        role="radio"
        aria-checked={selected}
        tabIndex={0}
        onClick={() => selectMode(mode)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && selectMode(mode)}
        sx={{
          flex: 1,
          minWidth: 180,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1,
          borderRadius: 2,
          cursor: "pointer",
          border: `1.5px solid ${selected ? theme.palette.primary.main : theme.palette.stroke}`,
          backgroundColor: selected ? alpha(theme.palette.primary.main, 0.06) : "transparent",
          transition: "all .15s",
          outline: "none",
          "&:hover": { borderColor: selected ? theme.palette.primary.main : theme.palette.text.disabled },
          "&:focus-visible": { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}` },
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            borderRadius: 1,
            display: "grid",
            placeItems: "center",
            color: selected ? "primary.main" : "text.secondary",
            backgroundColor: selected
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.text.primary, 0.05),
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography fontSize={14} fontWeight={600}>
              {title}
            </Typography>
            {badge && (
              <Chip
                label={badge}
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 16, fontSize: 11 }}
              />
            )}
          </Box>
          <Typography fontSize={12} color="text.secondary">
            {description}
          </Typography>
        </Box>
        {selected ? (
          <RadioButtonChecked color="primary" fontSize="small" />
        ) : (
          <RadioButtonUnchecked sx={{ color: "text.disabled" }} fontSize="small" />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 600, color: "text.primary" }}>E-mail</Typography>
      <Divider />

      {!healthy && value.enabled && (
        <Alert severity="warning" variant="outlined">
          O serviço de e-mail está com instabilidade. Suas configurações serão salvas normalmente.
        </Alert>
      )}

      <Box
        onClick={() => patch({ enabled: !value.enabled })}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: 2,
          cursor: "pointer",
          userSelect: "none",
          border: `1.5px solid ${value.enabled ? theme.palette.primary.main : theme.palette.stroke}`,
          backgroundColor: value.enabled ? alpha(theme.palette.primary.main, 0.06) : "transparent",
          transition: "all .2s",
          "&:hover": { borderColor: value.enabled ? theme.palette.primary.main : theme.palette.text.disabled },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            color: value.enabled ? theme.palette.common.white : "text.secondary",
            backgroundColor: value.enabled
              ? theme.palette.primary.main
              : alpha(theme.palette.text.primary, 0.06),
            transition: "all .2s",
          }}
        >
          <MailOutline sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500 }}>
          Enviar relatório por e-mail automaticamente
        </Typography>
        <Switch
          size="small"
          checked={value.enabled}
          inputProps={{ "aria-label": "Enviar relatório por e-mail automaticamente" }}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => patch({ enabled: e.target.checked })}
        />
      </Box>

      <Collapse in={value.enabled} unmountOnExit>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 0.5 }}>
          {isEditing && (
            <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
              Alterações valem para os próximos relatórios gerados.
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <RecipientsInput
              value={value.recipients}
              onChange={(recipients) => patch({ recipients })}
            />
            {noActive && (
              <Typography fontSize={12} color="error.main">
                Todos os destinatários estão pausados
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>Mensagem</Typography>
            <Box role="radiogroup" sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {modeCard("default", <AutoAwesomeOutlined fontSize="small" />, "Padrão", "Pronta para usar", "Recomendado")}
              {modeCard("custom", <EditNoteOutlined fontSize="small" />, "Personalizada", "Escreva do seu jeito")}
            </Box>
          </Box>

          <Collapse in={custom} unmountOnExit>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <TemplateEditor
                variant="subject"
                label="Assunto"
                value={value.assunto}
                onChange={(assunto) => patch({ assunto })}
                variables={variables}
                placeholder="Assunto do e-mail"
                error={subjectTooLong}
                helper={
                  subjectTooLong && (
                    <Typography fontSize={12} color="error.main">
                      Assunto muito longo ({value.assunto.length}/{MAX_SUBJECT})
                    </Typography>
                  )
                }
              />
              <TemplateEditor
                variant="body"
                label="Corpo do e-mail"
                value={value.corpo}
                onChange={(corpo) => patch({ corpo })}
                variables={variables}
                placeholder="Escreva sua mensagem"
                error={bodyTooBig}
                helper={
                  bodyTooBig && (
                    <Typography fontSize={12} color="error.main">
                      Mensagem grande demais. Remova imagens coladas no texto.
                    </Typography>
                  )
                }
              />

              {preview && (preview.avisos.length > 0 || preview.htmlFoiAlterado) && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                  {preview.avisos.map((token) => (
                    <Chip
                      key={token}
                      size="small"
                      color="warning"
                      variant="outlined"
                      icon={<WarningAmberOutlined fontSize="small" />}
                      label={`"{{${token}}}" não existe e ficará em branco`}
                    />
                  ))}
                  {preview.htmlFoiAlterado && (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      icon={<WarningAmberOutlined fontSize="small" />}
                      label="Parte da formatação foi removida"
                    />
                  )}
                </Box>
              )}

              <Link
                component="button"
                type="button"
                underline="hover"
                color="text.secondary"
                onClick={restoreDefault}
                sx={{ alignSelf: "flex-start", fontSize: 13 }}
              >
                Restaurar mensagem padrão
              </Link>
            </Box>
          </Collapse>
        </Box>
      </Collapse>
    </Box>
  );
}
